import { Tooltip } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { toastError } from '@components/Toast';

import { brandListApi, useGetCRMBrandListQuery } from '@features/crm/brands/api/brandListApi';
import {
	buildBrandListQueryKey,
	mergeBrandListPage,
	setBrandListActiveTab,
	setBrandListScrollY
} from '@features/crm/brands/redux/brandListSlice';
import { currentStageColumns } from '@features/crm/utils/helpers';

import { storeTotalBrandsCount } from '@store/actions/crm/CRMCommonAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import {
	_getKey,
	changeDateFormat,
	convertNumberToUSFormat,
	getDaysAgo,
	getNumberUnit,
	getNumberUnitForPCS,
	isCRMAdmin,
	isCRMManagement,
	isPageReachBottom
} from '@utils/Util';

const BrandList = () => {
	const [counts, setCounts] = useState(null);
	const [pageNumber, setPageNumber] = useState(0);
	const filterData = useSelector((store) => store.filterFieldStore);
	const loader = useSelector((store) => store.loaderStore);
	const activeTab = useSelector((store) => store.brandListStore.activeTab);
	const listResponse = useSelector((store) => store.brandListStore.listResponse);
	const listQueryKey = useSelector((store) => store.brandListStore.listQueryKey);
	const savedCurrentPage = useSelector((store) => store.brandListStore.currentPage);
	const listScrollY = useSelector((store) => store.brandListStore.listScrollY);
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const brandsRef = useRef();
	const skipPageNumberEffectRef = useRef(false);

	const generateParams = (pageNum) => {
		let params = `?page=${pageNum}&status=${activeTab}`;
		if (filterData.brand) {
			params += `&parentId=${filterData.brand[0].key}`;
		}
		if (filterData.brandType) {
			params += `&brandType=${filterData.brandType[0].key}`;
		}
		if (filterData.name) {
			params += `&name=${filterData.name[0].key}`;
		}
		if (filterData.sort) {
			params += `&sort=${filterData.sort[0].key}`;
		} else {
			params += `&sort=statusUpdatedAt,desc`;
		}
		return params;
	};

	const listFiltersKey = buildBrandListQueryKey(filterData, activeTab);
	const skipPage0Query = listQueryKey === listFiltersKey && listResponse !== null;

	const {
		data: page0Data,
		isSuccess: page0Success,
		isError: page0Error,
		error: page0Err,
		isFetching
	} = useGetCRMBrandListQuery(generateParams(0), {
		skip: skipPage0Query,
		refetchOnMountOrArgChange: true
	});

	const brands = listResponse?.data ?? [];

	const goToBrandProfile = useCallback(
		(id) => {
			dispatch(setBrandListScrollY(window.scrollY));
			navigate(`/brand/profile/${id}`);
		},
		[dispatch, navigate]
	);

	const fetchBrands = useCallback(
		async (page) => {
			const qKey = buildBrandListQueryKey(filterData, activeTab);
			try {
				dispatch(openLoader());
				const data = await dispatch(
					brandListApi.endpoints.getCRMBrandList.initiate(generateParams(page), {
						subscribe: false
					})
				).unwrap();
				dispatch(storeTotalBrandsCount(data.totalElements));
				dispatch(mergeBrandListPage({ queryKey: qKey, page, response: data }));
				dispatch(closeLoader());
			} catch (err) {
				dispatch(closeLoader());
				const msg = err?.data?.message ?? err?.message ?? 'Something went wrong';
				toastError(msg);
			}
		},
		[dispatch, filterData, activeTab]
	);

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = brandsRef.current || {};
			if (totalElements > 0 && totalElements !== data?.length) {
				setPageNumber(currentPage + 1);
			}
		}
	};

	const fetchBrandWiseCount = async () => {
		try {
			const { data } = await ApiClient.GET('brandWiseCount');
			setCounts(data);
		} catch (err) {
			toastError(err.response.data.message);
		}
	};

	useEffect(() => {
		window.addEventListener('scroll', handleScroll);
		fetchBrandWiseCount();
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		if (!skipPage0Query && isFetching) {
			dispatch(openLoader());
		}
	}, [skipPage0Query, isFetching, dispatch]);

	useEffect(() => {
		if (skipPage0Query || !page0Success || !page0Data || isFetching) return;
		dispatch(storeTotalBrandsCount(page0Data.totalElements));
		dispatch(mergeBrandListPage({ queryKey: listFiltersKey, page: 0, response: page0Data }));
		setPageNumber(0);
		dispatch(closeLoader());
	}, [page0Data, page0Success, skipPage0Query, listFiltersKey, dispatch, isFetching]);

	useEffect(() => {
		if (skipPage0Query || !page0Error) return;
		dispatch(closeLoader());
		const msg = page0Err?.data?.message ?? page0Err?.message ?? 'Something went wrong';
		toastError(msg);
	}, [skipPage0Query, page0Error, page0Err, dispatch]);

	useEffect(() => {
		brandsRef.current = listResponse;
	}, [listResponse]);

	useEffect(() => {
		const qKey = buildBrandListQueryKey(filterData, activeTab);
		const cached = listQueryKey === qKey && listResponse !== null;
		if (cached) {
			brandsRef.current = listResponse;
			if (typeof listResponse.totalElements === 'number') {
				dispatch(storeTotalBrandsCount(listResponse.totalElements));
			}
			if (savedCurrentPage !== 0) {
				skipPageNumberEffectRef.current = true;
				setPageNumber(savedCurrentPage);
			}
			dispatch(closeLoader());
			return;
		}
		window.scrollTo(0, 0);
		dispatch(setBrandListScrollY(0));
		dispatch(brandListApi.util.invalidateTags(['CRMBrandList']));
	}, [filterData, activeTab, dispatch]);

	// useLayoutEffect(() => {
	// 	const qKey = buildBrandListQueryKey(filterData, activeTab);
	// 	if (listQueryKey !== qKey || listResponse == null || listScrollY <= 0) return;
	// 	console.log('listScrollY', listScrollY);
	// 	window.scrollTo({ top: listScrollY, behavior: 'auto' });
	// }, [listQueryKey, listFiltersKey, listResponse, listScrollY, filterData, activeTab]);

	useEffect(() => {
		if (listScrollY > 0) {
			window.scrollTo({ top: listScrollY, behavior: 'auto' });
		}
	}, [listScrollY]);

	useEffect(() => {
		if (skipPageNumberEffectRef.current) {
			skipPageNumberEffectRef.current = false;
			return;
		}
		if (pageNumber !== 0) {
			fetchBrands(pageNumber);
		}
	}, [pageNumber, fetchBrands]);

	const generateNameFromList = (dataList) => {
		if (!dataList) return;
		let nameList = [];
		dataList.forEach((item) => {
			nameList.push(item.name);
		});
		return nameList.join(', ');
	};

	const renderTableBody = () => {
		return brands.map((item) => {
			return (
				<tr key={`brand_id_${_getKey()}`} onClick={() => goToBrandProfile(item.id)}>
					<td>
						<div className="d-flex align-items-center">
							<div className="logo">
								{item.brandLogoDocResponse?.docUrl && (
									<img
										src={item.brandLogoDocResponse?.docUrl}
										alt="brand-logo"
										className="logo"
										style={{ width: '100px', height: 'auto', objectFit: 'contain' }}
									/>
								)}
							</div>
							<div className="name-location ml-2">
								<p className="regular-14 weight-500 gray_dark_01">
									{item.name}{' '}
									{item?.hasAnyRunningActivity && (
										<Tooltip title="Running" placement="top" arrow>
											<img
												src={`${process.env.PUBLIC_URL}/icons/waves.svg`}
												alt="wave"
												className="ml-12"
											/>
										</Tooltip>
									)}
								</p>
								{item?.locationResponseList?.[0] && (
									<p className="regular-12 gray_dark_02">
										{item?.locationResponseList?.[0]?.city},{' '}
										{item?.locationResponseList?.[0]?.countryResponse?.name}
									</p>
								)}
							</div>
						</div>
					</td>
					<td>
						<p className="regular-14 gray_dark_02">{item.group}</p>
						<p className="regular-14 gray_dark_02">{item.type}</p>
					</td>
					{activeTab === 'ON_BOARDING' && (
						<td>
							<p className="regular-14 gray_dark_02">{item.currentOnBoardingStage}</p>
						</td>
					)}

					<td>
						<p className="regular-14 gray_dark_02 mb-1">
							A/C:{' '}
							<span className="gray_dark_01">
								{generateNameFromList(item.accountManagerList)}
							</span>
						</p>
						<p className="regular-14 gray_dark_02 mb-1">
							PM:{' '}
							<span className="gray_dark_01">
								{generateNameFromList(item.projectManagerList)}
							</span>
						</p>
						<p className="regular-14 gray_dark_02 mb-1">
							MR:{' '}
							<span className="gray_dark_01">
								{generateNameFromList(item.merchandiserList)}
							</span>
						</p>
						<p className="regular-14 gray_dark_02">
							BYR:{' '}
							<span className="gray_dark_01 white-space-wrap">
								{generateNameFromList(item.buyerList)}
							</span>
						</p>
					</td>
					{['ACTIVE', 'ARCHIVE'].includes(activeTab) && (
						<td>
							<p className="regular-14 gray_dark_02 mb-1">
								Total Styles:
								<span className="gray_dark_01">
									&nbsp;{getNumberUnitForPCS(item?.nitexDesign + item?.buyerDesign, true)}{' '}
									PCS.
								</span>
							</p>
							<p className="regular-14 gray_dark_02 mb-1">
								Nitex Styles:
								<span className="gray_dark_01">
									{' '}
									{getNumberUnitForPCS(item?.nitexDesign, true)} PCS.
								</span>
							</p>
							<p className="regular-14 gray_dark_02">
								Total Qty:
								<span className="gray_dark_01">
									{' '}
									{getNumberUnitForPCS(item?.totalQuantity, true)} PCS.
								</span>
							</p>
							<p className="regular-14 gray_dark_02 mb-1">
								PO value:
								<span className="gray_dark_01">
									{item?.achievedAmount > 0
										? ` $${getNumberUnit(item?.achievedAmount)} `
										: 'N/A'}
								</span>
							</p>
						</td>
					)}
					{!['ON_BOARDED', 'ON_BOARDING', 'PIPELINE'].includes(activeTab) && (
						<td>
							{item.firstOrderDate && (
								<p className="regular-14 gray_dark_02 mb-2">
									{changeDateFormat(item.firstOrderDate, 'YYYY-MM-DD', 'DD MMM, YYYY')}{' '}
									<span className="gray_dark_03 d-block">
										({getDaysAgo(item.firstOrderDate, 'YYYY-MM-DD')})
									</span>
								</p>
							)}
							{item.lastOrderDate && (
								<p className="regular-14 gray_dark_01">
									{changeDateFormat(item.lastOrderDate, 'YYYY-MM-DD', 'DD MMM, YYYY')}{' '}
									<span className="gray_dark_03 d-block">
										({getDaysAgo(item.lastOrderDate, 'YYYY-MM-DD')})
									</span>{' '}
								</p>
							)}
						</td>
					)}
					{activeTab === 'PIPELINE' && <td />}
					<td>
						<p className="regular-14 gray_dark_02">
							{item?.marketValue > 0 ? ` $${getNumberUnit(item?.marketValue)} ` : ''}
						</p>
					</td>
					<td>
						<p className="regular-14 gray_dark_02">
							{item?.annualProduction > 0 ? ` $${getNumberUnit(item?.annualProduction)} ` : ''}
						</p>
					</td>
					<td>
						<p className="regular-14 gray_dark_02">
							{item?.noOfOutlet > 0
								? ` ${convertNumberToUSFormat(item?.noOfOutlet, true)} `
								: ''}
						</p>
					</td>
				</tr>
			);
		});
	};

	const onTabChange = (value) => {
		if (value === activeTab) return;
		setPageNumber(0);
		dispatch(setBrandListActiveTab(value));
	};

	const renderTabs = () => {
		let tmpCurrentStages = currentStageColumns;
		if (isCRMAdmin()) {
			tmpCurrentStages = [...currentStageColumns, { label: 'Pending', value: 'PENDING' }];
		}
		return tmpCurrentStages?.map((item) => {
			if (item.value !== 'ARCHIVE' || (item.value === 'ARCHIVE' && isCRMManagement())) {
				return (
					<li
						key={`brand_tab_${item.value}`}
						onClick={() => onTabChange(item.value)}
						className={activeTab === item.value ? 'active' : ''}
					>
						{item.label} ({counts?.[item.value] ? counts?.[item.value] : '0'})
					</li>
				);
			}
		});
	};

	return (
		<section className="body-content pt-0">
			<div className="brand-overview-with-tab">
				<div className="brand-tab-container">
					<div
						className={`brandlist-inner-tabs inner-tabs pb-2 mb-1 bg-gray ${
							location.pathname === '/brands/list' ? 'on-brand-list' : ''
						}`}
					>
						<ul>{renderTabs()}</ul>
					</div>

					<div className="scroll-x-label">
						<table className="table with-border brand-profile-list-table">
							<thead>
								<tr>
									<th>Brand name</th>
									<th>Type</th>
									{activeTab === 'ON_BOARDING' && <th>On-boarding Status</th>}
									<th>Team &amp; buyer Info</th>
									{!['ON_BOARDED', 'ON_BOARDING', 'PIPELINE', 'PENDING'].includes(
										activeTab
									) && <th>Style overview </th>}
									{!['ON_BOARDED', 'ON_BOARDING', 'PIPELINE'].includes(activeTab) && (
										<th>First &amp; Last order</th>
									)}
									{activeTab === 'PIPELINE' && <th>Running Activities</th>}
									<th>Market value ($)</th>
									<th>APN ($)</th>
									<th>Stores world wide</th>
								</tr>
							</thead>
							<tbody>{renderTableBody()}</tbody>
						</table>
						{!loader && brands.length === 0 && (
							<p className="alert common-empty-state">No brand found</p>
						)}
					</div>
				</div>
			</div>
		</section>
	);
};

export default BrandList;
